import { TDInputOTP } from '../../lib/funnels/components/v1/default/input-otp'
import { queryParamsToSession } from '../../lib/funnels/functions/v1/default/queryParamsToSession'
import { getFunnelFromUrl } from '../../lib/funnels/functions/v1/default/getFunnelFromUrl'
import { getSessionValue } from '../../lib/funnels/functions/v1/default/getSessionValue'
import { updateSession } from '../../lib/funnels/functions/v1/default/updateSession'
import { resetOtpLoader } from '../../lib/funnels/functions/v1/html/resetOtpLoader'
import { hideError } from '../../lib/funnels/functions/v1/default/hideError'
import { submitSpinner } from '../../lib/funnels/functions/v1/default/submitSpinner'
import { resetSubmitSpinner } from '../../lib/funnels/functions/v1/default/resetSubmitSpinner'
import { submitCodeInternal } from '../../lib/funnels/functions/v1/submit/submitCodeInternal'
import { submitPhoneInternal } from '../../lib/funnels/functions/v1/submit/submitPhoneInternal'
import { formatPhone } from '../../lib/funnels/functions/v1/default/formatPhone'
import { STATES } from '../../lib/funnels/constants/states'
import { resolvePostSubmitUrl } from '../../lib/funnels/functions/v1/submit/resolvePostSubmitUrl'
import { submitForm } from '../../lib/funnels/functions/v1/submit/submitForm'
import { leadDataToParam } from '@turbodebt/turbo-shared'
import { getFunnelCache } from '../../lib/funnels/functions/v1/default/getFunnelCache'
import { setUpFormInputs } from '../../lib/funnels/functions/v1/html/setupFormInputs'
import { checkInputValidity } from '../../lib/funnels/functions/v1/html/checkInputValidity'
import { showInputError } from '../../lib/funnels/functions/v1/html/showInputError'
import { hideInputError } from '../../lib/funnels/functions/v1/html/hideInputError'
import { checkSelectValidity } from '../../lib/funnels/functions/v1/html/checkSelectValidity'
import { showSelectError } from '../../lib/funnels/functions/v1/html/showSelectError'
import { captureSubmitEvent } from '../../lib/funnels/functions/v1/submit/captureSubmitEvent'
import { captureSubmitSentTo } from '../../lib/funnels/functions/v1/submit/captureSubmitSentTo'
import { getTrustedFormFields } from '../../lib/funnels/functions/v1/default/getTrustedFormFields'

import { submitCodeVeracity } from '../../lib/funnels/functions/v1/submit/submitCodeVeracity'
import { submitPhoneVeracity } from '../../lib/funnels/functions/v1/submit/submitPhoneVeracity'
import { stripPhone } from '../../lib/funnels/functions/v1/default/stripPhone'

// Define custom elements
export function defineCustomElements() {
  customElements.define('td-input-otp', TDInputOTP)
}

// Session constants
export const SESSION_ID = 'tdMainFunnel'

/**
 * Initialize session with query parameters and default values
 */
export function initializeSession() {
  queryParamsToSession(SESSION_ID)

  const funnelFromParam = getSessionValue(SESSION_ID, 'funnel')
  if (!funnelFromParam) {
    const funnel = getFunnelFromUrl()
    updateSession(SESSION_ID, 'funnel', funnel)
  } else {
    updateSession(SESSION_ID, 'website', funnelFromParam)
  }

  const debtOwed = getSessionValue(SESSION_ID, 'debtOwed')
  if (!debtOwed) {
    updateSession(SESSION_ID, 'debtOwed', '7500')
  }
}

/**
 * Populate state dropdown with options
 * @param {HTMLSelectElement} stateSelect - The select element to populate
 */
export function populateStateOptions(stateSelect) {
  if (stateSelect) {
    STATES.forEach((state) => {
      const option = document.createElement('option')
      option.value = state.abbreviation
      option.textContent = state.name
      stateSelect.appendChild(option)
    })
  }
}

/**
 * Set up Google Maps address autocomplete
 * @param {string} addressElementId - ID of the address input element
 * @param {string} cityElementId - ID of the city input element
 * @param {string} stateElementId - ID of the state select element
 * @param {string} zipcodeElementId - ID of the zipcode input element
 */
export function setupGoogleMaps(
  addressElementId = 'addressLine',
  cityElementId = 'city',
  stateElementId = 'state',
  zipcodeElementId = 'zipcode',
) {
  const setupMaps = () => {
    // @ts-ignore
    if (window.google?.maps?.places) {
      // @ts-ignore
      window.initGoogleMaps(
        document.getElementById(addressElementId),
        document.getElementById(cityElementId),
        document.getElementById(stateElementId),
        document.getElementById(zipcodeElementId),
        (
          /** @type {{ addressLine: string; city: string; state: string; zipCode: string; }} */
          addressData,
        ) => {
          updateSession(SESSION_ID, 'addressLine', addressData.addressLine)
          updateSession(SESSION_ID, 'city', addressData.city)
          updateSession(SESSION_ID, 'state', addressData.state)
          updateSession(SESSION_ID, 'zipcode', addressData.zipCode)
        },
      )
    } else {
      setTimeout(setupMaps, 100)
    }
  }
  setupMaps()
  document.addEventListener('googleMapsLoaded', setupMaps)
}

/**
 * Set up address toggle functionality
 * @param {HTMLElement} addressToggle - The toggle button element
 */
export function setupAddressToggle(addressToggle) {
  if (addressToggle) {
    const originalText = addressToggle.innerHTML

    addressToggle.addEventListener('click', () => {
      const addressFields = document.getElementById('addressFields')
      addressFields?.classList.toggle('hidden')
      addressToggle.classList.toggle('open')

      if (addressToggle.classList.contains('open')) {
        addressToggle.textContent = 'Back to address search'
      } else {
        addressToggle.innerHTML = originalText
      }
    })
  }
}

/**
 * Sets up the OTP UI with phone number display and reset functionality
 * @param {string} phoneNumber - The phone number to display and use for resending OTP
 * @param {Function} transitionToFrame - Function to transition between frames
 * @param {number} phoneFrameNumber - The frame number to transition to when editing phone (default: 3)
 * @param {boolean} useVeracity - Whether to use Veracity API for verification
 */
export function setupOtpUI(
  phoneNumber,
  transitionToFrame,
  phoneFrameNumber = 3,
  useVeracity = false,
) {
  const otpInput = document.getElementById('otp')
  const shadowRoot = otpInput?.shadowRoot

  if (otpInput && shadowRoot) {
    const resendOtpInstructions = shadowRoot.querySelector('.instructions')
    if (resendOtpInstructions instanceof HTMLElement) {
      resendOtpInstructions.innerHTML = `USE THE TEXT THAT WAS SENT TO <span>${formatPhone(phoneNumber)}</span>`
    }

    const resetButtonsContainer = shadowRoot.querySelector('.reset-buttons')
    if (resetButtonsContainer instanceof HTMLElement) {
      resetButtonsContainer.innerHTML = ''

      const resetButton = document.createElement('button')
      const editPhoneButton = document.createElement('button')
      const countdown = document.createElement('span')

      resetButton.disabled = true
      resetButton.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 512 512"><path d="M463.5 224l8.5 0c13.3 0 24-10.7 24-24l0-128c0-9.7-5.8-18.5-14.8-22.2s-19.3-1.7-26.2 5.2L413.4 96.6c-87.6-86.5-228.7-86.2-315.8 1c-87.5 87.5-87.5 229.3 0 316.8s229.3 87.5 316.8 0c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0c-62.5 62.5-163.8 62.5-226.3 0s-62.5-163.8 0-226.3c62.2-62.2 162.7-62.5 225.3-1L327 183c-6.9 6.9-8.9 17.2-5.2 26.2s12.5 14.8 22.2 14.8l119.5 0z"/></svg> Resend Code'

      resetButton.addEventListener('click', async function () {
        const phoneRaw = phoneNumber.replace(/\D/g, '') || ''

        if (useVeracity) {
          await submitPhoneVeracity(
            phoneRaw,
            SESSION_ID,
            window.location.pathname,
          )
        } else {
          await submitPhoneInternal(
            phoneRaw,
            SESSION_ID,
            window.location.pathname,
          )
        }
      })

      editPhoneButton.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 448 512"><path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/></svg> Edit Phone'
      editPhoneButton.addEventListener('click', function () {
        // Clear any OTP error state when going back to edit phone
        if (otpInput) {
          hideError(otpInput)
        }

        // Reset any loading spinner on the phone frame button
        const phoneFrame = document.querySelector(
          `.onboarding-form-frame[data-frame="${phoneFrameNumber}"]`,
        )
        if (phoneFrame) {
          const phoneButton = /** @type {HTMLElement | null} */ (
            phoneFrame.querySelector('.btn-continue')
          )
          if (phoneButton) {
            resetSubmitSpinner(phoneButton, 'Continue')
          }
        }

        transitionToFrame(phoneFrameNumber)
      })

      resetButtonsContainer.appendChild(editPhoneButton)
      resetButtonsContainer.appendChild(resetButton)
      resetButtonsContainer.appendChild(countdown)

      // Start countdown (2 minutes for savings-v2, 3 seconds for others)
      startInitialCountdown(resetButton, countdown)
    }
  }
}

/**
 * Start the initial countdown (3 seconds for most funnels, 2 minutes for savings-v2)
 * @param {HTMLButtonElement} resetButton - The reset button
 * @param {HTMLElement} countdown - The countdown display element
 */
function startInitialCountdown(resetButton, countdown) {
  const isSavingsV2 = window.location.pathname.includes(
    '/onboarding/savings-v2',
  )
  const totalSeconds = isSavingsV2 ? 120 : 3 // 2 minutes for savings-v2, 3 seconds for others
  let seconds = totalSeconds

  const interval = setInterval(() => {
    if (isSavingsV2) {
      // Format as minutes:seconds for 2-minute countdown
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      const formatted = `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
      countdown.innerHTML = `in <strong>${formatted}</strong>`
    } else {
      // Format as seconds for 3-second countdown
      countdown.innerHTML = `in <strong>${seconds}</strong> seconds`
    }

    seconds--
    if (seconds < 0) {
      clearInterval(interval)
      resetButton.disabled = false
      countdown.innerHTML = ''
    }
  }, 1000)
}

/**
 * Set up OTP input event listener
 * @param {HTMLElement} otp - The OTP input element
 * @param {Function} transitionToFrame - Function to transition between frames
 */
export function setupOtpListener(otp, transitionToFrame, useVeracity = false) {
  let isSubmitting = false // Flag to prevent multiple simultaneous submissions

  otp?.addEventListener('input', async (e) => {
    const target = /** @type {HTMLInputElement} */ (e.target)
    if (!target) {
      throw Error('unexpected: target missing')
    }
    const code = target.value
    const phone = getSessionValue(SESSION_ID, 'phone')

    // Prevent multiple simultaneous submissions
    if (isSubmitting) {
      return
    }

    if (code && phone) {
      isSubmitting = true // Set flag to prevent multiple submissions

      if (otp) {
        otp.shadowRoot
          ?.querySelector('.small-loader')
          ?.setAttribute('style', 'opacity: 1')
      }

      const phoneFormatted = phone.replace(/\D/g, '') || phone

      try {
        const success = useVeracity
          ? await submitCodeVeracity(code, phoneFormatted)
          : await submitCodeInternal(code, phoneFormatted)

        if (success) {
          const otpFrame = /** @type {HTMLElement | null} */ (
            otp.closest('.onboarding-form-frame')
          )
          const nextFrame = otpFrame
            ? parseInt(otpFrame.dataset.frame || '0') + 1
            : 5
          transitionToFrame(nextFrame)
        } else if (otp) {
          resetOtpLoader(otp)
        }
      } finally {
        isSubmitting = false // Reset flag after submission completes
      }
    }
  })
}

/**
 * Create a progress update function
 * @param {HTMLElement|null} progressIndicator - The progress indicator element
 * @param {HTMLElement|null} progressBarFill - The progress bar fill element
 * @param {number} totalFrames - Total number of question frames
 * @param {number} loadingFrameNumber - The frame number of the loading screen
 * @param {number|null} otpFrameNumber - The frame number for OTP
 * @returns {Function} - The update progress function
 */
export function createProgressUpdater(
  progressIndicator,
  progressBarFill,
  totalFrames,
  loadingFrameNumber,
  otpFrameNumber = null,
) {
  /** @type {ReturnType<typeof setInterval> | null} */
  let animationInterval = null

  return function updateProgress(/** @type {number} */ currentFrame) {
    // Clear any existing animation
    if (animationInterval) {
      clearInterval(animationInterval)
      animationInterval = null
    }

    if (progressIndicator) {
      progressIndicator.style.display = 'block'
    }

    // Handle loading frame with animation loop
    if (currentFrame === loadingFrameNumber) {
      if (progressIndicator) {
        progressIndicator.style.visibility = 'hidden'
      }

      if (progressBarFill) {
        let progress = 0
        const animationSpeed = 30 // milliseconds (~60fps)
        const increment = 1 // percentage per step

        // Remove any CSS transitions that might interfere
        progressBarFill.style.transition = 'none'

        animationInterval = setInterval(() => {
          progress += increment
          if (progress >= 100) {
            progress = 0
          }
          progressBarFill.style.width = `${progress}%`
        }, animationSpeed)
      }
      return
    }

    const progressFrame = currentFrame
    let questionsCompleted = progressFrame - 1

    // Handle OTP frame logic
    if (otpFrameNumber !== null && currentFrame === otpFrameNumber) {
      // OTP frame shows same progress as phone frame
      questionsCompleted = Math.max(0, currentFrame - 2)
    } else if (otpFrameNumber !== null && currentFrame > otpFrameNumber) {
      questionsCompleted = currentFrame - 2 // Account for phone+OTP being one step
    }

    if (currentFrame >= loadingFrameNumber) {
      questionsCompleted = totalFrames
    }

    const progress = (questionsCompleted / totalFrames) * 100

    if (progressIndicator) {
      progressIndicator.textContent = `${questionsCompleted} OUT OF ${totalFrames} QUESTIONS ANSWERED`
    }

    if (progressBarFill) {
      progressBarFill.style.width = `${progress}%`
    }
  }
}

/**
 * Create a frame transition function
 * @param {Function} updateProgress - The progress update function
 * @param {Function|null} onLoadingFrame - Optional callback for when loading frame is reached
 * @returns {Function} - The transition function
 */
export function createFrameTransition(updateProgress, onLoadingFrame = null) {
  let currentFrame = 1

  /**
   * Transition to a different frame
   * @param {number} nextFrameNumber - The frame number to transition to
   */
  return function transitionToFrame(nextFrameNumber) {
    const currentFrameEl = document.querySelector(
      `.onboarding-form-frame[data-frame="${currentFrame}"]`,
    )
    const nextFrameEl = document.querySelector(
      `.onboarding-form-frame[data-frame="${nextFrameNumber}"]`,
    )

    if (!currentFrameEl || !nextFrameEl) return

    currentFrameEl.classList.add('exit')
    currentFrameEl.classList.remove('active')

    setTimeout(() => {
      nextFrameEl.classList.add('active')
      nextFrameEl.classList.remove('exit')
      currentFrame = nextFrameNumber
      updateProgress(currentFrame)

      // Scroll to top of page on frame transition
      window.scrollTo({ top: 0, behavior: 'smooth' })

      if (onLoadingFrame && typeof onLoadingFrame === 'function') {
        onLoadingFrame(currentFrame)
      }
    }, 500)
  }
}

/**
 * Get frame count and loading frame number from HTML
 * @returns {{ questionFrames: number, loadingFrameNumber: number, allFrameNumbers: number[] }}
 */
export function getFrameInfo() {
  const frameElements = document.querySelectorAll(
    '.onboarding-form-frame[data-frame]',
  )
  const frameNumbers = Array.from(frameElements).map((el) => {
    const htmlEl = /** @type {HTMLElement} */ (el)
    return parseInt(htmlEl.dataset.frame || '0')
  })

  const loadingFrameNumber = Math.max(...frameNumbers) // Highest frame number is loading
  const questionFrames = frameNumbers.length - 1 // All frames except the loading frame

  return {
    questionFrames,
    loadingFrameNumber,
    allFrameNumbers: frameNumbers,
  }
}

// Use the shared setUpFormInputs function which handles IMask formatting and validation
setUpFormInputs()

/**
 * Set up input event listeners to clear error state and update button colors
 */
export function setupInputListeners() {
  /** @type {NodeListOf<HTMLInputElement>} */
  const inputs = document.querySelectorAll('.onboarding-form-input')
  inputs.forEach((input) => {
    // Use input event only for button color update
    input.addEventListener('input', () => {
      updateButtonColor(input)
    })

    input.addEventListener('blur', () => {
      if (input.value.trim()) {
        // Use shared validation functions
        if (checkInputValidity(input)) {
          hideInputError(input)
        } else {
          showInputError(input)
        }
      } else {
        // If input is empty, show error
        showInputError(input)
      }

      // Update button color based on validation state
      updateButtonColor(input)
    })
  })

  // Set up listeners for Google autocomplete filled fields
  setupGoogleAutocompleteListeners()

  // Initialize button colors for all frames
  initializeButtonColors()
}

/**
 * Set up listeners for Google autocomplete filled fields
 */
function setupGoogleAutocompleteListeners() {
  // Listen for when Google autocomplete fills address fields
  const addressLine = document.getElementById('addressLine')
  const city = document.getElementById('city')
  const state = document.getElementById('state')
  const zipcode = document.getElementById('zipcode')

  if (addressLine) {
    // Listen for blur event which fires when Google autocomplete fills the field
    addressLine.addEventListener('blur', () => {
      setTimeout(() => updateButtonColorFromAddress(), 100)
    })
  }

  if (city) {
    city.addEventListener('blur', () => {
      setTimeout(() => updateButtonColorFromAddress(), 100)
    })
  }

  if (state) {
    state.addEventListener('change', () => {
      setTimeout(() => updateButtonColorFromAddress(), 100)
    })
  }

  if (zipcode) {
    zipcode.addEventListener('blur', () => {
      setTimeout(() => updateButtonColorFromAddress(), 100)
    })
  }
}

/**
 * Update button color when address fields are filled by Google autocomplete
 */
function updateButtonColorFromAddress() {
  const currentFrame = document.querySelector('.onboarding-form-frame.active')
  if (currentFrame) {
    const inputs = currentFrame.querySelectorAll('.onboarding-form-input')
    if (inputs.length > 0) {
      updateButtonColor(/** @type {HTMLInputElement} */ (inputs[0]))
    }
  }
}

/**
 * Initialize button colors for all frames based on current input values
 */
function initializeButtonColors() {
  const frames = document.querySelectorAll('.onboarding-form-frame')
  frames.forEach((frame) => {
    const inputs = frame.querySelectorAll('.onboarding-form-input')
    if (inputs.length > 0) {
      // Use the first input to trigger button color update
      updateButtonColor(/** @type {HTMLInputElement} */ (inputs[0]))
    }
  })
}

/**
 * Update button color based on input validation state
 * @param {HTMLInputElement} input - The input element to validate
 */
export function updateButtonColor(input) {
  const currentFrame = input.closest('.onboarding-form-frame')
  if (!currentFrame) return

  const button = currentFrame.querySelector('.btn-continue')
  if (!button) return

  // Get all inputs in the current frame
  const inputs = currentFrame.querySelectorAll('.onboarding-form-input')
  let allValid = true

  inputs.forEach((frameInput) => {
    const inputElement = /** @type {HTMLInputElement} */ (frameInput)
    const value = inputElement.value.trim()

    if (!value) {
      allValid = false
      return
    }

    if (!checkInputValidity(inputElement)) {
      allValid = false
    }
  })

  // Check address fields using shared validation logic
  if (allValid && currentFrame.querySelector('#addressLine')) {
    const addressFields = document.getElementById('addressFields')
    const city = /** @type {HTMLInputElement | null} */ (
      document.getElementById('city')
    )
    const state = /** @type {HTMLSelectElement | null} */ (
      document.getElementById('state')
    )
    const zipcode = /** @type {HTMLInputElement | null} */ (
      document.getElementById('zipcode')
    )

    if (!addressFields?.classList.contains('hidden')) {
      // Validate city input
      if (city && !checkInputValidity(city)) {
        allValid = false
      }

      // Validate state select
      if (state && !checkSelectValidity(state)) {
        allValid = false
      }

      // Validate zipcode input
      if (zipcode && !checkInputValidity(zipcode)) {
        allValid = false
      }
    }
  }

  // Update button classes
  button.classList.remove('valid')

  if (allValid) {
    button.classList.add('valid')
  }
}

/**
 * Set up continue button event listeners
 * @param {Function} handleFrameSubmit - The frame submission handler
 */
export function setupContinueButtons(handleFrameSubmit) {
  document.querySelectorAll('.btn-continue').forEach((button) => {
    button.addEventListener('click', async () => {
      /** @type {HTMLElement | null} */
      const currentFrameEl = button.closest('.onboarding-form-frame')
      if (currentFrameEl) {
        await handleFrameSubmit(currentFrameEl)
      }
    })
  })
}

/**
 * Set up enter key event listeners on inputs
 * @param {Function} handleFrameSubmit - The frame submission handler
 */
export function setupEnterKeyListeners(handleFrameSubmit) {
  document.querySelectorAll('.onboarding-form-input').forEach((input) => {
    input.addEventListener(
      'keypress',
      /** @param {Event} evt */ async (evt) => {
        const e = /** @type {KeyboardEvent} */ (evt)
        if (e.key === 'Enter') {
          e.preventDefault()
          /** @type {HTMLElement | null} */
          const currentFrameEl = input.closest('.onboarding-form-frame')
          await handleFrameSubmit(currentFrameEl)
        }
      },
    )
  })
}

/**
 * Set up previous button event listeners
 * @param {Function} transitionToFrame - The frame transition function
 */
export function setupPreviousButtons(transitionToFrame) {
  document
    .querySelectorAll('.onboarding-pointer-previous')
    .forEach((button) => {
      button.addEventListener('click', () => {
        /** @type {HTMLElement | null} */
        const currentFrameEl = button.closest('.onboarding-form-frame')

        if (currentFrameEl) {
          const frame = currentFrameEl.dataset.frame || '0'
          const prevFrame = parseInt(frame) - 1

          // Check if we're going to the OTP frame and reset it
          const prevFrameEl = document.querySelector(
            `.onboarding-form-frame[data-frame="${prevFrame}"]`,
          )
          const isGoingToOtpFrame = prevFrameEl?.querySelector('#otp')
          const isLeavingStep1Frame =
            prevFrameEl?.querySelector('#button-step3')
          if (isGoingToOtpFrame) {
            const otpInput = document.getElementById('otp')
            if (otpInput) {
              resetOtpLoader(otpInput)
              hideError(otpInput)
            }
          }
          if (isLeavingStep1Frame) {
            try {
              sessionStorage.setItem('onboarding-v3-1-left-via-click', '1')
            } catch {
              // nothing
            }
          }

          // Reset phone button loader when going back to phone frame
          const isGoingToPhoneFrame = prevFrameEl?.querySelector('#phone')
          if (isGoingToPhoneFrame) {
            const phoneInput = document.getElementById('phone')
            if (phoneInput) {
              const phoneFrame = phoneInput.closest('.onboarding-form-frame')
              if (phoneFrame) {
                const phoneButton = /** @type {HTMLElement | null} */ (
                  phoneFrame.querySelector('.btn-continue')
                )
                if (phoneButton) {
                  resetSubmitSpinner(phoneButton, 'Continue')
                }
              }
            }
          }

          transitionToFrame(prevFrame)
        }
      })
    })
}

/**
 * Set up sidebar toggle functionality
 * @param {HTMLElement|null} sidebar - The sidebar element
 * @param {HTMLElement|null} sidebarTarget - The sidebar target element
 */
export function setupSidebarToggle(sidebar, sidebarTarget) {
  let sidebarHeight = -1
  const sidebarAnimationLength = 40

  function toggleSidebarAnimation(closing = false) {
    let animationStep = 0
    if (sidebar && sidebarTarget) {
      const classListFunction = closing ? 'remove' : 'add'
      if (!closing) {
        sidebar.classList[classListFunction]('visible')
      }

      const animation = window.setInterval(() => {
        animationStep++
        const stepHeight = !closing
          ? (sidebarHeight / sidebarAnimationLength) * animationStep
          : sidebarHeight -
            (sidebarHeight / sidebarAnimationLength) * animationStep

        sidebar.style.height = `${stepHeight}px`
        sidebarTarget.style.top = `${stepHeight}px`

        if (animationStep == sidebarAnimationLength) {
          window.clearInterval(animation)
          if (closing) {
            sidebar.classList[classListFunction]('visible')
          }
        }
      }, sidebarAnimationLength / 10)
    }
  }

  function closeSidebar() {
    if (sidebar?.classList.contains('visible')) {
      toggleSidebarAnimation(true)
    }
  }

  sidebarTarget?.addEventListener('click', (_event) => {
    if (sidebarHeight === -1 && sidebar) {
      sidebar?.classList.add('calculating')
      sidebarHeight = sidebar.clientHeight
      sidebar.classList.remove('calculating')
      toggleSidebarAnimation()
    } else if (sidebar?.classList.contains('visible')) {
      toggleSidebarAnimation(true)
    } else {
      toggleSidebarAnimation()
    }
  })

  document.addEventListener('click', (event) => {
    if (sidebar?.classList.contains('visible')) {
      const isClickInsideSidebar = sidebar.contains(
        /** @type {Node} */ (event.target),
      )
      const isClickOnTarget = sidebarTarget?.contains(
        /** @type {Node} */ (event.target),
      )

      if (!isClickInsideSidebar && !isClickOnTarget) {
        closeSidebar()
      }
    }
  })

  const globalWindow = /** @type {any} */ (window)
  globalWindow.closeSidebar = closeSidebar
}

/**
 * Create a frame submission handler with validation
 * @param {Function} transitionToFrame - The frame transition function
 * @param {Function|null} setupOtpUIFn - The OTP UI setup function (optional if skipPhoneVerification is true)
 * @param {number} phoneFrameNumber - The frame number to transition to when editing phone (default: 3)
 * @param {boolean} useVeracity - Whether to use Veracity API for verification
 * @param {boolean} skipPhoneVerification - Whether to skip phone OTP verification entirely
 * @returns {Function} - The frame submission handler
 */
export function createFrameSubmitHandler(
  transitionToFrame,
  setupOtpUIFn = null,
  phoneFrameNumber = 3,
  useVeracity = false,
  skipPhoneVerification = false,
) {
  /**
   * Handles the form submission for the current frame
   * @param {HTMLElement | null} currentFrameEl - The current frame element being submitted
   */
  return async function handleFrameSubmit(currentFrameEl) {
    if (!currentFrameEl) return

    // Get all inputs in the current frame
    const inputElements = currentFrameEl.querySelectorAll('input')

    if (inputElements.length === 0) return

    // Flag to track if all inputs are valid
    let allInputsValid = true
    let phoneInput = null

    // Validate all inputs
    for (let i = 0; i < inputElements.length; i++) {
      const input = /** @type {HTMLInputElement} */ (inputElements[i])

      if (!input.value.trim()) {
        showInputError(input)
        allInputsValid = false
        continue
      }

      // Use shared validation function
      if (!checkInputValidity(input)) {
        showInputError(input)
        allInputsValid = false
      } else {
        // Save valid input to session
        updateSession(SESSION_ID, input.id, input.value.trim())

        // Special handling for fullName to also save as 'name'
        if (input.id === 'name') {
          updateSession(SESSION_ID, 'name', input.value.trim())
        }

        // Store phone input for OTP setup if it's valid
        if (input.id === 'phone') {
          phoneInput = input
          // Strip phone formatting and update both input and session
          const strippedPhone = stripPhone(input.value)
          input.value = strippedPhone
          updateSession(SESSION_ID, 'phone', strippedPhone)
        }
      }
    }

    // Check for address field validation if it's visible
    if (allInputsValid && currentFrameEl.querySelector('#addressLine')) {
      const addressFields = document.getElementById('addressFields')
      const city = /** @type {HTMLInputElement | null} */ (
        document.getElementById('city')
      )
      const state = /** @type {HTMLSelectElement | null} */ (
        document.getElementById('state')
      )
      const zipcode = /** @type {HTMLInputElement | null} */ (
        document.getElementById('zipcode')
      )

      if (!addressFields?.classList.contains('hidden')) {
        // Validate city input
        if (city && !checkInputValidity(city)) {
          showInputError(city)
          allInputsValid = false
        }

        // Validate state select
        if (state && !checkSelectValidity(state)) {
          showSelectError(state)
          allInputsValid = false
        }

        // Validate zipcode input
        if (zipcode && !checkInputValidity(zipcode)) {
          showInputError(zipcode)
          allInputsValid = false
        }

        // If all address fields are valid, save to session
        if (allInputsValid && city && state && zipcode) {
          updateSession(SESSION_ID, 'city', city.value)
          updateSession(SESSION_ID, 'state', state.value)
          updateSession(SESSION_ID, 'zipcode', zipcode.value)
        }
      }
    }

    // Update button color based on validation result
    const button = currentFrameEl.querySelector('.btn-continue')
    if (button) {
      button.classList.remove('valid')
      if (allInputsValid) {
        button.classList.add('valid')
      }
    }

    // Auto-open address fields if validation failed and toggle is not open
    if (!allInputsValid) {
      const addressToggle = document.getElementById('toggleAddress')
      if (!addressToggle?.classList.contains('open')) {
        addressToggle?.click()
      }
      return // Don't proceed if validation failed
    }

    // If all inputs are valid, proceed
    if (allInputsValid) {
      // Handle phone OTP if we have a valid phone input
      if (phoneInput) {
        const phoneRaw = phoneInput.value.replace(/\D/g, '') || ''

        if (!skipPhoneVerification) {
          const phoneButton = /** @type {HTMLElement | null} */ (
            currentFrameEl.querySelector('.btn-continue')
          )

          // Show loading spinner on the button
          if (phoneButton) {
            submitSpinner(phoneButton, 'Sending code...')
          }

          try {
            if (useVeracity) {
              await submitPhoneVeracity(
                phoneRaw,
                SESSION_ID,
                window.location.pathname,
              )
            } else {
              await submitPhoneInternal(
                phoneRaw,
                SESSION_ID,
                window.location.pathname,
              )
            }

            if (setupOtpUIFn) {
              setupOtpUIFn(
                phoneRaw,
                transitionToFrame,
                phoneFrameNumber,
                useVeracity,
              )
            }
          } catch {
            // Reset spinner on error
            if (phoneButton) {
              resetSubmitSpinner(phoneButton, 'Continue')
            }
            // Show error on phone input
            showInputError(phoneInput)
            return // Don't proceed to next frame on error
          }
        }
      }

      // Transition to next frame
      const nextFrame = parseInt(currentFrameEl.dataset.frame || '0') + 1
      transitionToFrame(nextFrame)
    }
  }
}

/**
 * Create a frame submission handler with validation for NDR flow
 * @param {Function} transitionToFrame - The frame transition function
 * @param {Function|null} setupOtpUIFn - The OTP UI setup function (optional if skipPhoneVerification is true)
 * @param {number} phoneFrameNumber - The frame number to transition to when editing phone (default: 3)
 * @param {boolean} useVeracity - Whether to use Veracity API for verification
 * @param {boolean} skipPhoneVerification - Whether to skip phone OTP verification entirely
 * @param {boolean} skipFirstFrameTransition - Whether to skip the first frame transition (form submit)
 * @returns {Function} - The frame submission handler
 */
export function createFrameSubmitHandlerNDR(
  transitionToFrame,
  setupOtpUIFn = null,
  phoneFrameNumber = 3,
  useVeracity = false,
  skipPhoneVerification = false,
  skipFirstFrameTransition,
) {
  /**
   * Handles the form submission for the current frame
   * @param {HTMLElement | null} currentFrameEl - The current frame element being submitted
   */
  return async function handleFrameSubmit(currentFrameEl) {
    if (!currentFrameEl) return

    // Get all inputs in the current frame
    const inputElements = currentFrameEl.querySelectorAll('input')

    if (inputElements.length === 0) return

    // Flag to track if all inputs are valid
    let allInputsValid = true
    let phoneInput = null

    // Validate all inputs
    for (let i = 0; i < inputElements.length; i++) {
      const input = /** @type {HTMLInputElement} */ (inputElements[i])

      if (!input.value.trim()) {
        showInputError(input)
        allInputsValid = false
        continue
      }

      // Use shared validation function
      if (!checkInputValidity(input)) {
        showInputError(input)
        allInputsValid = false
      } else {
        // Save valid input to session
        updateSession(SESSION_ID, input.id, input.value.trim())

        // Special handling for fullName to also save as 'name'
        if (input.id === 'name') {
          updateSession(SESSION_ID, 'name', input.value.trim())
        }

        // Store phone input for OTP setup if it's valid
        if (input.id === 'phone') {
          phoneInput = input
          // Strip phone formatting and update both input and session
          const strippedPhone = stripPhone(input.value)
          input.value = strippedPhone
          updateSession(SESSION_ID, 'phone', strippedPhone)
        }
      }
    }

    // Check for address field validation if it's visible
    if (allInputsValid && currentFrameEl.querySelector('#addressLine')) {
      const addressFields = document.getElementById('addressFields')
      const city = /** @type {HTMLInputElement | null} */ (
        document.getElementById('city')
      )
      const state = /** @type {HTMLSelectElement | null} */ (
        document.getElementById('state')
      )
      const zipcode = /** @type {HTMLInputElement | null} */ (
        document.getElementById('zipcode')
      )

      if (!addressFields?.classList.contains('hidden')) {
        // Validate city input
        if (city && !checkInputValidity(city)) {
          showInputError(city)
          allInputsValid = false
        }

        // Validate state select
        if (state && !checkSelectValidity(state)) {
          showSelectError(state)
          allInputsValid = false
        }

        // Validate zipcode input
        if (zipcode && !checkInputValidity(zipcode)) {
          showInputError(zipcode)
          allInputsValid = false
        }

        // If all address fields are valid, save to session
        if (allInputsValid && city && state && zipcode) {
          updateSession(SESSION_ID, 'city', city.value)
          updateSession(SESSION_ID, 'state', state.value)
          updateSession(SESSION_ID, 'zipcode', zipcode.value)
        }
      }
    }

    // Update button color based on validation result
    const button = currentFrameEl.querySelector('.btn-continue')
    if (button) {
      button.classList.remove('valid')
      if (allInputsValid) {
        button.classList.add('valid')
      }
    }

    // Auto-open address fields if validation failed and toggle is not open
    if (!allInputsValid) {
      const addressToggle = document.getElementById('toggleAddress')
      if (!addressToggle?.classList.contains('open')) {
        addressToggle?.click()
      }
      return // Don't proceed if validation failed
    }

    // If all inputs are valid, proceed
    if (allInputsValid) {
      // Handle phone OTP if we have a valid phone input
      if (phoneInput) {
        const phoneRaw = phoneInput.value.replace(/\D/g, '') || ''

        if (!skipPhoneVerification) {
          const phoneButton = /** @type {HTMLElement | null} */ (
            currentFrameEl.querySelector('.btn-continue')
          )

          // Show loading spinner on the button
          if (phoneButton) {
            submitSpinner(phoneButton, 'Sending code...')
          }

          try {
            if (useVeracity) {
              await submitPhoneVeracity(
                phoneRaw,
                SESSION_ID,
                window.location.pathname,
              )
            } else {
              await submitPhoneInternal(
                phoneRaw,
                SESSION_ID,
                window.location.pathname,
              )
            }

            if (setupOtpUIFn) {
              setupOtpUIFn(
                phoneRaw,
                transitionToFrame,
                phoneFrameNumber,
                useVeracity,
              )
            }
          } catch {
            // Reset spinner on error
            if (phoneButton) {
              resetSubmitSpinner(phoneButton, 'Continue')
            }
            // Show error on phone input
            showInputError(phoneInput)
            return // Don't proceed to next frame on error
          }
        }
      }

      // Transition to next frame
      if (skipFirstFrameTransition) {
        skipFirstFrameTransition = false
        return
      }
      const nextFrame = parseInt(currentFrameEl.dataset.frame || '0') + 1
      transitionToFrame(nextFrame)
    }
  }
}

/**
 * Set up disclaimer popup functionality
 */
export function setupDisclaimerPopup() {
  const disclaimerButton = document.querySelector('.btn-disclaimer')
  const disclaimerPopup = document.getElementById('disclaimerPopup')

  function closeDisclaimer() {
    if (disclaimerPopup?.classList.contains('visible')) {
      disclaimerPopup.classList.remove('visible')
      disclaimerPopup.classList.add('hidden')
      disclaimerButton?.classList.remove('open')
    }
  }

  if (disclaimerButton && disclaimerPopup) {
    disclaimerButton.addEventListener('click', () => {
      const isVisible = disclaimerPopup.classList.contains('visible')

      if (isVisible) {
        closeDisclaimer()
      } else {
        disclaimerPopup.classList.remove('hidden')
        disclaimerPopup.classList.add('visible')
        disclaimerButton.classList.add('open')
      }
    })

    document.addEventListener('click', (event) => {
      if (disclaimerPopup?.classList.contains('visible')) {
        const isClickInsidePopup = disclaimerPopup.contains(
          /** @type {Node} */ (event.target),
        )
        const isClickOnButton = disclaimerButton?.contains(
          /** @type {Node} */ (event.target),
        )

        if (!isClickInsidePopup && !isClickOnButton) {
          closeDisclaimer()
        }
      }
    })

    const globalWindow = /** @type {any} */ (window)
    globalWindow.closeDisclaimer = closeDisclaimer
  }
}

/**
 * Create a form submission handler
 * @returns {Function} - The form submission handler
 */
export function createFormSubmitHandler() {
  /**
   * Handles the final form submission with a timeout
   */
  return async function handleFormSubmit() {
    let xxTrustedFormCertUrl = ''
    try {
      const trustedFormData = getTrustedFormFields('onboarding-form')
      xxTrustedFormCertUrl = trustedFormData.xxTrustedFormCertUrl || ''
    } catch {
      console.warn('TrustedForm fields not found')
    }

    const detail = {
      addressLine: getSessionValue(SESSION_ID, 'addressLine'),
      city: getSessionValue(SESSION_ID, 'city'),
      state: getSessionValue(SESSION_ID, 'state'),
      zipcode: getSessionValue(SESSION_ID, 'zipcode'),
      phone: stripPhone(getSessionValue(SESSION_ID, 'phone')),
      email: getSessionValue(SESSION_ID, 'email'),
      birthdate: getSessionValue(SESSION_ID, 'birthdate'),
      fullName: getSessionValue(SESSION_ID, 'fullName'),
      debtOwed: getSessionValue(SESSION_ID, 'debtOwed') || '9999',
      xxTrustedFormCertUrl,
    }

    const sessionCache = getFunnelCache() || {}

    const base64Param = leadDataToParam({ ...detail, ...sessionCache })
    const thanksUrl = `https://funnels.turbodebt.com/thanks/?params=${base64Param}`

    captureSubmitEvent()
    try {
      const redirectUrl = await submitForm(detail)
      const href = resolvePostSubmitUrl(redirectUrl, thanksUrl)
      captureSubmitSentTo(/thanks/i.test(href) ? 'thanks' : 'chat')
      window.location.href = href
    } catch (error) {
      console.error('submit error:', error)
      captureSubmitSentTo('thanks')
      window.location.href = thanksUrl
    }
  }
}
